import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-secondary border border-accent/30',
        outline:
          'border-2 border-accent/40 bg-parchment text-primary hover:bg-accent/10 hover:border-accent',
        ghost: 'hover:bg-accent/10 text-primary',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        gold: 'bg-accent text-primary font-semibold hover:bg-accent-light border border-accent',
      },
      size: {
        default: 'h-11 px-4 py-2 text-sm',
        sm: 'h-9 px-3 text-xs',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  ),
);
Button.displayName = 'Button';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input
      className={cn(
        'flex h-11 w-full rounded-lg border-2 border-border bg-white/80 px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 disabled:opacity-50',
        className,
      )}
      ref={ref}
      {...props}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    className={cn(
      'flex min-h-[96px] w-full rounded-lg border-2 border-border bg-white/80 px-3 py-2 text-sm text-primary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 disabled:opacity-50',
      className,
    )}
    ref={ref}
    {...props}
  />
));
Textarea.displayName = 'Textarea';

export const Label = ({ className, ...props }: React.LabelHTMLAttributes<HTMLLabelElement>) => (
  <label className={cn('mb-1 block text-sm font-medium text-primary', className)} {...props} />
);

export function Card({
  className,
  children,
  ornate = true,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { ornate?: boolean }) {
  return (
    <div
      className={cn(
        ornate ? 'ornate-card rounded-xl p-4' : 'rounded-xl border border-border bg-white p-4 shadow-sm',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function Badge({
  children,
  variant = 'default',
  className,
}: {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'destructive' | 'gold';
  className?: string;
}) {
  /* Rozet renkleri de paletten geliyor. Tailwind'in hazır yeşil, kehribar
     ve kırmızı tonları bordo-yaldız düzenin yanında yabancı duruyordu. */
  const variants = {
    default: 'bg-muted text-primary border border-border',
    success: 'bg-success/10 text-success border border-success/35',
    warning: 'bg-accent/15 text-primary border border-accent/45',
    destructive: 'bg-destructive/10 text-destructive border border-destructive/30',
    gold: 'bg-accent/15 text-primary border border-accent/40',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        variants[variant],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'h-5 w-5 animate-spin rounded-full border-2 border-accent/30 border-t-accent',
        className,
      )}
    />
  );
}

export function Select({
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        'flex h-11 w-full rounded-lg border-2 border-border bg-white/80 px-3 text-sm text-primary focus-visible:border-accent focus-visible:outline-none',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}
